import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Sparkles, BookOpen } from "lucide-react";
import { useGate, useQuantumJourney } from "@/lib/quantum";
import { useSpace } from "@/lib/space";
import { slugify } from "@/lib/intro";
import { Page } from "@/components/app/AppKit";
import TeachingCards from "@/components/quantum/TeachingCards";
import GuidedPractice from "@/components/quantum/GuidedPractice";
import { Reflect, Checkin } from "@/components/quantum/ReflectCheckin";
import PracticeJournal from "@/components/temple/PracticeJournal";

const STAGES = [
  ["teach", "Teachings", (s) => s.cards.length],
  ["practice", "Practice", (s) => s.practices.length],
  ["reflect", "Reflect", (s) => s.reflections.length],
  ["checkin", "Check-in", () => true],
  ["weeks", "Weeks", (s, space) => space.weeks],
];

const Notice = ({ testId, icon: Icon, title, text }) => (
  <div data-testid={testId} className="mt-10 border border-[#D9B47A]/20 p-7">
    <Icon className="w-5 h-5 text-[#D9B47A]" strokeWidth={1.5} />
    <p className="mt-4 font-display text-2xl">{title}</p>
    <p className="mt-2 text-[15px] text-[#CFC2B6]">{text}</p>
    <Link to={useSpace().base} className="mt-6 inline-block font-tt-mono text-[12px] uppercase tracking-[0.2em] text-[#D9B47A] underline underline-offset-4">The gate map</Link>
  </div>
);

const Journey = ({ data, onComplete, initialPractice }) => {
  const space = useSpace();
  const seg = data.segments;
  const stages = STAGES.filter(([, , has]) => has(seg, space));
  const [stage, setStage] = useState(initialPractice != null && seg.practices.length ? "practice" : stages[0][0]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stage]);
  const nextStage = () => setStage(stages[Math.min(stages.length - 1, stages.findIndex(([k]) => k === stage) + 1)][0]);
  return (
    <>
      {seg.intro && stage === "teach" && <p data-testid="qi-gate-intro" className="mt-6 text-[16px] leading-relaxed text-[#CFC2B6]">{seg.intro}</p>}
      <nav data-testid="qi-stages" className="mt-8 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0,1fr))` }}>
        {stages.map(([k, label], j) => (
          <button key={k} data-testid={`qi-stage-${k}`} onClick={() => setStage(k)} aria-current={stage === k} className={`border-t-2 pt-2 text-left transition-colors ${stage === k ? "border-[#E8838E] text-[#F2E8DF]" : "border-[#D9B47A]/20 text-[#A8998C] hover:text-[#F2E8DF]"}`}>
            <span className="block font-tt-mono text-[10px] text-[#8F8176]">0{j + 1}</span>
            <span className="block font-tt-mono text-[10px] sm:text-[11px] uppercase tracking-[0.04em] sm:tracking-[0.12em] leading-tight">{label}</span>
          </button>
        ))}
      </nav>
      {stage === "teach" && <TeachingCards cards={seg.cards} onDone={nextStage} doneLabel={seg.practices.length ? "Begin the practice" : "Continue"} />}
      {stage === "practice" && <GuidedPractice g={data.g} practices={seg.practices} onDone={nextStage} initial={initialPractice} />}
      {stage === "reflect" && <Reflect g={data.g} prompts={seg.reflections} saved={data.responses?.answers} savedReply={data.responses?.reflect_reply} onDone={nextStage} />}
      {stage === "checkin" && <Checkin g={data.g} checkin={seg.checkin} saved={data.responses?.checkin} onComplete={onComplete} next={data.next} />}
      {stage === "weeks" && <div data-testid="gate-weeks" className="-mt-10"><PracticeJournal i={data.g} /></div>}
    </>
  );
};

// One gate's Quantum layer as an interactive journey: teachings, practice, reflection, check-in.
export default function QuantumGate() {
  const g = Number(useParams().g) || 0;
  const [params] = useSearchParams();
  const practice = params.get("practice");
  const { data, loading, error } = useGate(g);
  const { refresh } = useQuantumJourney();
  const space = useSpace();
  if (loading) return <Page testId="gate-loading"><Loader2 className="mt-16 w-5 h-5 animate-spin text-[#D9B47A]" /></Page>;
  if (error) return <Page testId="gate-error" title="Not here."><p className="mt-4 text-[#CFC2B6]">{error}</p><Link to={space.base} className="mt-6 inline-block text-[#D9B47A] underline">The gate map</Link></Page>;
  return (
    <Page testId="quantum-gate" eyebrow={`Gate ${String(g + 1).padStart(2, "0")} · ${space.layer}`} title={data.name}>
      <p data-testid="qi-gate-line" className="mt-4 font-display italic text-xl leading-relaxed text-[#E8838E]">{data.line}</p>
      {space.weeks && !data.locked && (
        <Link to={`/app/gates/${slugify(data.name)}`} data-testid="gate-full-guide" className="mt-4 inline-flex items-center gap-2 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C] hover:text-[#D9B47A] transition-colors"><BookOpen className="w-3.5 h-3.5" /> Read the full guide</Link>
      )}
      {data.locked ? (
        <Notice testId="qi-locked" icon={Lock} title="This gate is waiting for you." text="Complete the check-in of the earlier gates and it will open." />
      ) : !data.segments ? (
        <Notice testId="qi-preparing" icon={Sparkles} title="Ariel is preparing this gate." text="Its teachings and practices will appear here very soon." />
      ) : (
        <Journey key={g} data={data} onComplete={refresh} initialPractice={practice != null ? Number(practice) : null} />
      )}
    </Page>
  );
}
